import { useCallback, useMemo, useState } from "react";

let nextCartId = 1;
const makeCartId = () => `cart_${nextCartId++}`;

/**
 * Local-only cart state for the dining-session menu tab.
 * Each line carries a stable `cartItemId` (used as React key) plus the menu item details.
 */
export function useCart() {
    const [cart, setCart] = useState([]);

    const addItem = useCallback((item) => {
        setCart((prev) => {
            const idx = prev.findIndex((c) => c.menu_item_id === item.id && !c.notes);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
                return next;
            }
            return [
                ...prev,
                {
                    cartItemId: makeCartId(),
                    menu_item_id: item.id,
                    name: item.name,
                    price: item.price,
                    image_url: item.image_url,
                    quantity: 1,
                    notes: "",
                },
            ];
        });
    }, []);

    const changeQuantity = useCallback((cartItemId, delta) => {
        setCart((prev) => {
            const updated = prev.map((line) =>
                line.cartItemId === cartItemId
                    ? { ...line, quantity: Math.max(0, line.quantity + delta) }
                    : line
            );
            return updated.filter((line) => line.quantity > 0);
        });
    }, []);

    const changeNotes = useCallback((cartItemId, notes) => {
        setCart((prev) => prev.map((line) => (line.cartItemId === cartItemId ? { ...line, notes } : line)));
    }, []);

    const removeItem = useCallback((cartItemId) => {
        setCart((prev) => prev.filter((line) => line.cartItemId !== cartItemId));
    }, []);

    const clear = useCallback(() => setCart([]), []);

    const subtotal = useMemo(
        () => cart.reduce((sum, line) => sum + line.price * line.quantity, 0),
        [cart]
    );
    const count = useMemo(
        () => cart.reduce((sum, line) => sum + line.quantity, 0),
        [cart]
    );

    return { cart, addItem, changeQuantity, changeNotes, removeItem, clear, subtotal, count };
}
